<template>
  <div ref="wrapper" class="base-multi-submit btn-group" :class="{ show: isMenuOpen }">
    <BaseSubmitButton
      :type="type"
      :text="text"
      :btn-class="primaryBtnClass"
      :is-submitting="isSubmitting"
      :disabled="disabled"
      @click="onPrimaryClick"
    >
      <template v-if="$slots.label" #default>
        <slot name="label" />
      </template>
    </BaseSubmitButton>
    <button
      v-if="$slots.options"
      type="button"
      :class="['base-multi-submit__toggle', btnClass]"
      :disabled="isSubmitting || disabled"
      :aria-expanded="isMenuOpen"
      aria-haspopup="true"
      @click.stop="toggleMenu"
    >
      <span class="base-multi-submit__caret" aria-hidden="true"></span>
      <span class="visually-hidden">{{ t('Toggle Dropdown') }}</span>
    </button>
    <div
      v-if="$slots.options"
      v-show="isMenuOpen"
      class="dropdown-menu dropdown-menu-end show base-multi-submit__menu"
      role="menu"
      @click="closeMenu"
    >
      <slot name="options" item-class="dropdown-item" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PropType, computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  btnClass: {
    type: String,
    default: 'btn btn-primary rounded-pill',
  },
  text: {
    type: String,
    default: '',
  },
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String as PropType<'submit' | 'reset' | 'button'>,
    default: 'submit',
  },
})

const emits = defineEmits<{
  (e: 'primary-click', event: MouseEvent): void
  (e: 'click', event: MouseEvent): void
}>()

const { t } = useI18n()

const isMenuOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)

const primaryBtnClass = computed(() => `base-multi-submit__primary ${props.btnClass}`)

const onPrimaryClick = (event: MouseEvent) => {
  // This implements primary buttons @click event and prevents that event from bubbling down to dropdown click
  emits('primary-click', event)
  emits('click', event)

  if (props.type === 'submit') {
    const buttonEl = event.currentTarget as HTMLButtonElement | null
    const form = buttonEl?.form
    if (form && typeof form.requestSubmit === 'function') {
      event.preventDefault()
      form.requestSubmit()
    }
  }
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const handleClickOutside = (event: MouseEvent) => {
  if (wrapper.value && !wrapper.value.contains(event.target as Node)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(
  () => props.isSubmitting || props.disabled,
  (isDisabled) => {
    if (isDisabled) closeMenu()
  },
)
</script>

<style scoped lang="scss">
.base-multi-submit {
  position: relative;
  border-radius: 999px;
  overflow: visible;
  display: inline-flex;
  align-items: stretch;
}

.base-multi-submit__primary {
  border-radius: 999px 0 0 999px !important;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  padding-inline: 1.5rem;
  margin: 0;
}

.base-multi-submit__toggle {
  border-radius: 0 999px 999px 0 !important;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-left: 1px solid rgba(255, 255, 255, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-inline: 0.75rem;
  width: 2.75rem;
  flex: 0 0 auto;
  margin: 0;
}

.base-multi-submit__caret {
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #fff;
}

.base-multi-submit__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 0.35rem);
  min-width: 100%;
  margin-top: 0;
}
</style>
